import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import type { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '@/stores/useNotificationStore';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { parseIeImportFile } from './ieImportFile';
import type { IeImportPayloadRow } from './ieImportFile';
import {
  ieImportService,
  type IeImportErrorDetail,
  type IeImportPreviewRow,
  type IeImportResponse,
} from '@/services/ieImportService';
import { exportIeErrorReport } from '@/utils/ieExport';
import { downloadBlankIeTemplate } from '@/utils/ieExport';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred.';
};

const getImportErrorDetails = (error: unknown): IeImportErrorDetail[] => {
  if (!isRecord(error)) return [];
  const responseData = isRecord((error as { response?: unknown }).response)
    ? (error as { response: Record<string, unknown> }).response
    : error;
  const errors = (responseData as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    return errors
      .filter((detail): detail is IeImportErrorDetail => isRecord(detail))
      .map((detail) => ({
        rowNumber: typeof detail.rowNumber === 'number' ? detail.rowNumber : null,
        column: typeof detail.column === 'string' ? detail.column : null,
        message: typeof detail.message === 'string' ? detail.message : 'Unknown error',
      }));
  }
  return [];
};

interface IeImportDialogProps {
  open: boolean;
  ieKitId: string;
  kitName: string;
  onClose: () => void;
  onImported?: () => void;
}

export const IeImportDialog: React.FC<IeImportDialogProps> = ({
  open,
  ieKitId,
  kitName,
  onClose,
  onImported,
}) => {
  const isViewer = useWriteGuard();
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const [rows, setRows] = useState<IeImportPayloadRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<IeImportErrorDetail[]>([]);
  const [validationResponse, setValidationResponse] = useState<IeImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setFileName(null);
      setIsParsingFile(false);
      setIsValidating(false);
      setIsImporting(false);
      setErrorMessage(null);
      setValidationErrors([]);
      setValidationResponse(null);
    }
  }, [open]);

  const handleSelectFile = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsParsingFile(true);
    setErrorMessage(null);
    setValidationErrors([]);
    setValidationResponse(null);

    try {
      const parsed = await parseIeImportFile(file);
      setRows(parsed.rows);
      setFileName(parsed.fileName);
    } catch (error) {
      setRows([]);
      setFileName(null);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsParsingFile(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  }, []);

  const handleValidate = useCallback(async () => {
    if (rows.length === 0) return;

    setIsValidating(true);
    setErrorMessage(null);
    setValidationErrors([]);
    setValidationResponse(null);

    try {
      const response = await ieImportService.validateImport(ieKitId, rows);
      setValidationResponse(response);
      notifications.success(
        'Validation Passed',
        `${response.summary.totalRows} rows validated successfully.`,
      );
    } catch (error) {
      const message = getErrorMessage(error);
      const details = getImportErrorDetails(error);
      setErrorMessage(message);
      setValidationErrors(details);
      notifications.error('Validation Failed', message);
    } finally {
      setIsValidating(false);
    }
  }, [rows, ieKitId, notifications]);

  const handleImport = useCallback(async () => {
    if (rows.length === 0 || !validationResponse || validationErrors.length > 0) {
      return;
    }

    setIsImporting(true);
    setErrorMessage(null);

    try {
      const response = await ieImportService.applyImport(ieKitId, rows);
      await queryClient.invalidateQueries({ queryKey: ['entities'] });
      await queryClient.invalidateQueries({ queryKey: ['ie-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['thr-items'] });
      await queryClient.invalidateQueries({ queryKey: ['phr-items'] });
      notifications.success(
        'Import Complete',
        `${response.summary.totalRows} items imported. ${response.createdCount} created, ${response.updatedCount} updated.`,
      );
      onImported?.();
      onClose();
    } catch (error) {
      const message = getErrorMessage(error);
      const details = getImportErrorDetails(error);
      setErrorMessage(message);
      setValidationErrors(details);
      notifications.error('Import Failed', message);
    } finally {
      setIsImporting(false);
    }
  }, [rows, ieKitId, validationResponse, validationErrors, queryClient, notifications, onImported, onClose]);

  const handleDownloadErrors = useCallback(() => {
    if (validationErrors.length === 0) return;
    exportIeErrorReport(
      validationErrors.map((err) => ({
        rowNumber: err.rowNumber ?? 0,
        lin: '',
        nsn: '',
        nomenclature: '',
        quantity: '',
        optional: '',
        notes: '',
        error: err.message,
      })),
      kitName,
    );
  }, [validationErrors, kitName]);

  const hasValidation = Boolean(validationResponse);
  const canValidate = rows.length > 0 && !isParsingFile && !isValidating && !isImporting;
  const canImport = hasValidation && validationErrors.length === 0 && !isImporting && !isValidating && !isViewer;
  const previewRows: IeImportPreviewRow[] = validationResponse?.preview ?? [];
  const previewRowsToDisplay = previewRows.slice(0, 10);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import {ieKitLabel} Items — {kitName}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a spreadsheet with {ieKitLabel.toLowerCase()} items. Required columns: <strong>LIN</strong> and{' '}
          <strong>Quantity</strong>. Optional columns: NSN, Nomenclature, Optional, Notes.
          LINs must match existing catalog items.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(event) => void handleSelectFile(event)}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={isParsingFile || isValidating || isImporting}
          >
            {isParsingFile ? 'Loading…' : fileName ? 'Choose Another File' : 'Choose File'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => downloadBlankIeTemplate(ieKitLabel)}
          >
            Download Template
          </Button>
          {fileName && (
            <Typography variant="body2" color="text.secondary">
              {fileName} ({rows.length} rows)
            </Typography>
          )}
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {hasValidation && validationResponse && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Validation passed: {validationResponse.summary.totalRows} rows valid.
          </Alert>
        )}

        {hasValidation && previewRowsToDisplay.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Preview of items to import
            </Typography>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>Row</TableCell>
                  <TableCell sx={{ width: 100 }}>LIN</TableCell>
                  <TableCell sx={{ width: 120 }}>NSN</TableCell>
                  <TableCell>Catalog Item</TableCell>
                  <TableCell sx={{ width: 60 }}>Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRowsToDisplay.map((row) => (
                  <TableRow key={`${row.rowNumber}-${row.catalogId}`}>
                    <TableCell>{row.rowNumber}</TableCell>
                    <TableCell>{row.lin}</TableCell>
                    <TableCell>{row.nsn ?? '—'}</TableCell>
                    <TableCell>{row.catalogDisplayName}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {previewRows.length > previewRowsToDisplay.length && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Showing first {previewRowsToDisplay.length} of {previewRows.length} rows.
              </Typography>
            )}
          </Box>
        )}

        {validationErrors.length > 0 && (
          <>
            <Alert severity="error" sx={{ mb: 1 }}>
              Validation returned {validationErrors.length} error
              {validationErrors.length === 1 ? '' : 's'}.
            </Alert>
            <Box sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadErrors}
                disabled={isValidating || isImporting}
              >
                Download Error Report
              </Button>
            </Box>
            <List dense sx={{ maxHeight: 280, overflowY: 'auto', mb: 1 }}>
              {validationErrors.slice(0, 25).map((detail, index) => {
                const rowLabel = detail.rowNumber ? `Row ${detail.rowNumber}` : 'General';
                const columnLabel = detail.column ? `, ${detail.column}` : '';
                return (
                  <ListItem key={`${detail.rowNumber ?? 'x'}-${detail.column ?? 'y'}-${index}`}>
                    <ListItemText primary={`${rowLabel}${columnLabel}: ${detail.message}`} />
                  </ListItem>
                );
              })}
            </List>
            {validationErrors.length > 25 && (
              <Typography variant="caption" color="text.secondary">
                Showing first 25 errors.
              </Typography>
            )}
          </>
        )}

        {(isValidating || isImporting) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                {isValidating ? 'Validating import…' : 'Applying import…'}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isParsingFile || isValidating || isImporting}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleValidate()}
          disabled={!canValidate || isViewer}
        >
          {isValidating ? 'Validating…' : 'Validate'}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleImport()}
          disabled={!canImport}
        >
          {isImporting ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
